import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { TlkLoadingSpinnerComponent } from '@itero/ui-toolkit-angular/loading-spinner';
import { PatientModule } from '@modules/patient/patient.module';
import { PatientAppIframeCommunicationService } from '@modules/patient/services/patient-app-iframe-communication.service';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { finalize, Observable, takeUntil, tap } from 'rxjs';
import { RxIntuitivePatientDetailsFacade } from './rx-intuitive-patient-details.facade';
import { PatientDisplayMode } from '@modules/patient/models/patient-display-mode.enum';

@Component({
	selector: 'rx-intuitive-patient-details',
	templateUrl: './rx-intuitive-patient-details.component.html',
	styleUrls: ['./rx-intuitive-patient-details.component.scss'],
	imports: [PatientModule, TlkButtonComponent, TlkCardComponent, TlkLoadingSpinnerComponent, TranslateModule, CommonModule],
	providers: [RxIntuitivePatientDetailsFacade],
	standalone: true
})
export class RxIntuitivePatientDetailsComponent extends BaseDestroyableComponent implements OnDestroy {
	@Input() isCreateDisabled = false;
	@Output() patientCreated = new EventEmitter<void>();

	readonly patientDisplayMode$: Observable<PatientDisplayMode> = this.facade.patientDisplayMode$;

	isPatientAppReady$ = this.facade.isPatientAppReady$;
	isValidToSave$ = this.facade.validatePatientDetailsForSave();
	isNewPatient$ = this.facade.isNewPatient$;
	isSaving = false;

	constructor(
		private readonly facade: RxIntuitivePatientDetailsFacade,
		private readonly patientAppIframeCommunicationService: PatientAppIframeCommunicationService
	) {
		super();
	}

	onCreateClick(): void {
		if (this.isSaving) {
			return;
		}

		this.isSaving = true;

		this.facade
			.savePatientDetails()
			.pipe(
				takeUntil(this.componentAlive$),
				tap(canProceed => {
					if (canProceed) {
						this.patientCreated.emit();
					}
				}),
				finalize(() => {
					this.isSaving = false;
				})
			)
			.subscribe();
	}

	ngOnDestroy(): void {
		this.setPatientAppReadyState(false);
		super.ngOnDestroy();
	}

	private setPatientAppReadyState(isReady: boolean): void {
		this.patientAppIframeCommunicationService.setPatientAppReadyState(isReady);
	}
}
